/**
 * SOP Upload Modal
 *
 * Simple single-step upload form for SOP PDFs.
 * Processing happens in the background after the modal closes.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import {
  X,
  FileText,
  Upload,
  Sparkles,
  File,
  Loader2,
} from 'lucide-react';
import {
  useCreateSop,
  useProcessSop,
  uploadPdfToS3,
} from '../../hooks/useSOPs';
import { useDepartmentLookup } from '../../hooks/useDepartments';

interface SOPUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (sopId: string) => void;
}

interface FormData {
  title: string;
  version: string;
  departmentId: string;
  file: File | null;
}

const initialFormData: FormData = {
  title: '',
  version: '1.0',
  departmentId: '',
  file: null,
};

export function SOPUploadModal({ isOpen, onClose, onSuccess }: SOPUploadModalProps) {
  // State
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Hooks
  const { data: departments } = useDepartmentLookup();
  const createSop = useCreateSop();
  const processSop = useProcessSop();

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setFormData(initialFormData);
        setError(null);
        setIsDragging(false);
        setIsUploading(false);
      }, 300);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isUploading) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isUploading, onClose]);

  // File handling
  const handleFileSelect = useCallback((file: File) => {
    if (file.type !== 'application/pdf') {
      setError('Please select a PDF file');
      return;
    }
    setError(null);
    setFormData((prev) => ({ ...prev, file }));
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  // Upload and trigger processing, then close immediately
  const handleUpload = async () => {
    if (!formData.title.trim() || !formData.file) {
      setError('Please provide a title and select a PDF file');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Step 1: Create SOP and get presigned URL
      const createResult = await createSop.mutateAsync({
        title: formData.title.trim(),
        version: formData.version || '1.0',
        departmentId: formData.departmentId || undefined,
        fileName: formData.file.name,
        contentType: 'application/pdf',
      });

      const newSopId = createResult.sopId;

      // Step 2: Upload PDF to S3
      const uploadSuccess = await uploadPdfToS3(
        createResult.uploadUrl,
        formData.file
      );

      if (!uploadSuccess) {
        throw new Error('Failed to upload PDF to storage');
      }

      // Step 3: Start async processing (don't await completion)
      processSop.mutate({ id: newSopId });

      // Close modal and show success toast
      onSuccess?.(newSopId);
      onClose();
      toast.success('SOP uploaded! Processing in background...', {
        description: 'You\'ll see the extracted jobs once processing completes.',
        duration: 5000,
      });

    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during upload');
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={!isUploading ? onClose : undefined}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
      >
        {/* Header */}
        <div className="relative px-8 pt-8 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00674a] to-[#00674a]/80 flex items-center justify-center shadow-lg shadow-[#00674a]/20">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                Upload SOP Document
              </h2>
              <p className="text-sm text-gray-500">
                Add a PDF to extract procedural jobs
              </p>
            </div>
          </div>

          {/* Close button */}
          {!isUploading && (
            <button
              onClick={onClose}
              className="absolute top-6 right-6 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-8 py-6">
          {/* Error display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <X className="w-3 h-3 text-red-500" />
              </div>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                SOP Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                disabled={isUploading}
                placeholder="e.g., Machine Setup Procedure"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-500 bg-white"
              />
            </div>

            {/* Version and Department row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Version
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, version: e.target.value }))
                  }
                  disabled={isUploading}
                  placeholder="1.0"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Department
                </label>
                <select
                  value={formData.departmentId}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, departmentId: e.target.value }))
                  }
                  disabled={isUploading}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 bg-white disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">All Departments</option>
                  {departments?.map((dept: { id: string; name: string }) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* File upload zone */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                PDF Document <span className="text-red-500">*</span>
              </label>
              <div
                onDrop={!isUploading ? handleDrop : undefined}
                onDragOver={!isUploading ? handleDragOver : undefined}
                onDragLeave={!isUploading ? handleDragLeave : undefined}
                onClick={() => !isUploading && fileInputRef.current?.click()}
                className={`
                  relative border-2 border-dashed rounded-2xl p-6 text-center
                  transition-all duration-300
                  ${isUploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                  ${
                    isDragging
                      ? 'border-[#00674a] bg-[#c5e8c2]/20 scale-[1.02]'
                      : formData.file
                      ? 'border-[#00674a] bg-[#c5e8c2]/10'
                      : 'border-gray-200 hover:border-[#00674a]/50 hover:bg-gray-50'
                  }
                `}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                  disabled={isUploading}
                  className="hidden"
                />

                {formData.file ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-xl bg-[#00674a] flex items-center justify-center">
                      <File className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{formData.file.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    {!isUploading && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormData((prev) => ({ ...prev, file: null }));
                        }}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove file
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                      <Upload className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-700 text-sm">
                        Drop your PDF here or click to browse
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        PDF files only, up to 50MB
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isUploading}
            className="px-5 py-2.5 text-gray-600 font-semibold hover:text-gray-900 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!formData.title.trim() || !formData.file || isUploading}
            className="px-6 py-2.5 bg-[#00674a] text-white font-semibold rounded-xl hover:bg-[#005540] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-[#00674a]/20"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Upload & Process
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SOPUploadModal;
